import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { GraduationCap, Sparkles, ArrowRight, LogOut, Zap, CreditCard, History, School, LayoutDashboard, Users } from "lucide-react";
import { SiteFooter } from "../components/LoginGate";
import { SCHOOLHUB_ENABLED } from "../lib/features";

const SAMPLE_TOPICS = ["Fotosynteza", "II wojna światowa", "Funkcje kwadratowe", "Mitologia grecka", "Układ krwionośny"];

export default function Landing({ onStart, onOpenPricing, onOpenHistory, onOpenClassroom, onOpenAdmin, onOpenSchool, onOpenLegal }) {
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const [topic, setTopic] = useState("");
  const [demoMode, setDemoMode] = useState(false);

  const remaining = subscription?.remaining ?? 0;
  const planName = subscription?.plan_name || "Darmowy";
  const isFree = subscription?.plan === "free";

  const submit = (e) => {
    e?.preventDefault?.();
    const t = topic.trim();
    if (!t && !demoMode) return;
    // Block if free plan and quota exhausted, unless in demo mode
    if (!demoMode && isFree && remaining <= 0) {
      onOpenPricing("quota");
      return;
    }
    onStart({ topic: t || "Fotosynteza", demo: demoMode });
  };

  const pickSample = (t) => setTopic(t);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="w-full border-b border-slate-100 sticky top-0 bg-white/80 backdrop-blur z-10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="logo">
            <div className="w-9 h-9 rounded-2xl bg-[#FFD600] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-extrabold tracking-tight text-slate-900">SocialStart</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {user?.is_admin && (
              <button
                onClick={onOpenAdmin}
                data-testid="header-admin-button"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-slate-900 text-white hover:bg-slate-800 rounded-full px-3 py-1.5"
                aria-label="Panel administratora"
              >
                <LayoutDashboard className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin</span>
              </button>
            )}
            <button
              onClick={onOpenHistory}
              data-testid="header-history-button"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-1.5"
              aria-label="Historia"
            >
              <History className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Historia</span>
            </button>            <button
              onClick={onOpenClassroom}
              data-testid="header-classroom-button"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-1.5"
              aria-label="Panel klasy"
            >
              <School className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Klasa</span>
            </button>
            <button
              onClick={onOpenSchool}
              data-testid="header-school-button"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#FFD600] text-slate-900 hover:bg-[#E6C200] rounded-full px-3 py-1.5"
              aria-label="Moja szkoła"
              style={SCHOOLHUB_ENABLED ? undefined : { display: "none" }}
            >
              <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Moja szkoła</span>
            </button>
            <button
              onClick={() => onOpenPricing()}
              data-testid="header-pricing-button"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-1.5"
            >
              <CreditCard className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Plany</span>
            </button>
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-slate-200" data-testid="user-avatar"/>
            )}
            <span className="hidden sm:inline text-sm text-slate-600" data-testid="user-name">{user?.name}</span>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="text-slate-500 hover:text-slate-900 p-2 rounded-xl hover:bg-slate-100"
              aria-label="Wyloguj się"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col justify-center">
        <div className="max-w-[900px] w-full mx-auto px-4 sm:px-6 py-10 sm:py-16 ss-fadein">
          {/* Quota / plan banner */}
          {subscription && (
            <div
              data-testid="quota-banner"
              className="flex items-center justify-between gap-3 mb-6 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3"
            >
              <div className="text-sm">
                <span className="text-slate-500">Plan </span>
                <b className="text-slate-900">{planName}</b>
                <span className="text-slate-500"> · pozostało </span>
                <b data-testid="remaining-count" className="text-slate-900">{remaining}</b>
                <span className="text-slate-500"> {remaining === 1 ? "quiz" : "quizów"}</span>
              </div>
              {isFree && (
                <button
                  onClick={() => onOpenPricing()}
                  data-testid="upgrade-button"
                  className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#FFD600] text-slate-900 hover:bg-[#E6C200]"
                >
                  Zobacz plany
                </button>
              )}
            </div>
          )}

          <div className="inline-flex items-center gap-2 bg-[#FEF9C3] border border-yellow-200 rounded-full px-3 py-1 text-xs font-semibold text-slate-900 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> AI ROBI ZA CIEBIE NOTATKI
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
            Czego chcesz się <br className="hidden sm:block"/>
            <span className="relative">
              dziś nauczyć?
              <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#FFD600] -z-10 rounded" />
            </span>
          </h1>

          <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl">
            Wpisz temat, a AI przygotuje dla Ciebie teorię i quiz.
          </p>

          <form onSubmit={submit} className="mt-8 sm:mt-10">
            <div className="relative rounded-3xl border-2 border-slate-200 focus-within:border-[#FFD600] transition-colors bg-white p-2 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.05)]">
              <textarea
                data-testid="topic-input"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Np. fotosynteza, II wojna światowa, funkcje kwadratowe..."
                className="w-full resize-none bg-transparent outline-none px-4 py-4 text-base sm:text-lg placeholder:text-slate-400 min-h-[110px] sm:min-h-[90px]"
                rows={3}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
                }}
              />
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-2 pb-2">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={demoMode}
                    onChange={(e) => setDemoMode(e.target.checked)}
                    className="accent-[#FFD600] w-4 h-4"
                    data-testid="demo-mode-toggle"
                  />
                  <Zap className="w-4 h-4 text-slate-400" />
                  Tryb demonstracyjny (bez AI, bez limitu)
                </label>
                <button
                  type="submit"
                  data-testid="start-learning-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold text-base px-6 py-3.5 rounded-2xl btn-yellow-glow"
                >
                  Rozpocznij naukę
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </form>

          <div className="mt-8">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Popularne tematy</p>
            <div className="flex flex-wrap gap-2">
              {SAMPLE_TOPICS.map((t) => (
                <button
                  key={t}
                  data-testid={`quick-topic-chip-${t.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g,"")}`}
                  onClick={() => pickSample(t)}
                  className="text-sm px-4 py-2 rounded-full border border-slate-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors text-slate-700"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Moja szkoła promo card */}
          {SCHOOLHUB_ENABLED && (
          <button
            onClick={onOpenSchool}
            data-testid="landing-school-card"
            className="mt-10 w-full text-left group bg-gradient-to-br from-[#FFD600] to-[#E6C200] rounded-3xl p-5 sm:p-6 flex items-center gap-4 hover:shadow-[0_10px_30px_-5px_rgba(255,214,0,0.5)] transition-shadow"
          >
            <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-[#FFD600]" strokeWidth={2.5}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-900/70">Nowość</div>
              <div className="text-lg sm:text-xl font-extrabold tracking-tight text-slate-900">
                Twoja szkoła — społeczność
              </div>
              <div className="text-sm text-slate-900/80 mt-0.5">
                Znajdź swoją szkołę, zobacz nauczycieli i uczniów, oceniaj i komentuj.
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-900 group-hover:translate-x-1 transition-transform flex-shrink-0" strokeWidth={2.5}/>
          </button>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-100">
        <SiteFooter onOpenLegal={onOpenLegal} />
      </footer>
    </div>
  );
}
